export interface FileValidationResult {
  isValid: boolean;
  error?: string;
  warnings?: string[];
}

export interface FileTypeConfig {
  extensions: string[];
  mimeTypes: string[];
  maxSize: number;
  magicNumbers?: { offset: number; bytes: number[] }[];
}

export class FileValidationService {
  private static readonly FILE_TYPE_CONFIGS: Record<string, FileTypeConfig> = {
    image: {
      extensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'],
      mimeTypes: [
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/bmp',
        'image/svg+xml',
      ],
      maxSize: 100 * 1024 * 1024,
      magicNumbers: [
        { offset: 0, bytes: [0xff, 0xd8, 0xff] },
        { offset: 0, bytes: [0x89, 0x50, 0x4e, 0x47] },
        { offset: 0, bytes: [0x47, 0x49, 0x46, 0x38] },
        { offset: 0, bytes: [0x52, 0x49, 0x46, 0x46] },
        { offset: 0, bytes: [0x42, 0x4d] },
      ],
    },
    video: {
      extensions: ['.mp4', '.avi', '.mov', '.webm', '.mkv', '.flv', '.wmv'],
      mimeTypes: [
        'video/mp4',
        'video/x-msvideo',
        'video/quicktime',
        'video/webm',
        'video/x-matroska',
        'video/x-flv',
        'video/x-ms-wmv',
      ],
      maxSize: 500 * 1024 * 1024,
      magicNumbers: [
        { offset: 4, bytes: [0x66, 0x74, 0x79, 0x70] },
        { offset: 0, bytes: [0x00, 0x00, 0x00] },
        { offset: 0, bytes: [0x1a, 0x45, 0xdf, 0xa3] },
        { offset: 0, bytes: [0x52, 0x49, 0x46, 0x46] },
      ],
    },
    encrypted: {
      extensions: [
        '.pdf',
        '.doc',
        '.docx',
        '.xls',
        '.xlsx',
        '.ppt',
        '.pptx',
        '.txt',
        '.zip',
        '.rar',
        '.7z',
      ],
      mimeTypes: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain',
        'application/zip',
        'application/x-rar-compressed',
        'application/x-7z-compressed',
        'application/octet-stream',
      ],
      maxSize: 1024 * 1024 * 1024,
    },
  };

  private static readonly DANGEROUS_EXTENSIONS = [
    '.exe',
    '.bat',
    '.cmd',
    '.com',
    '.pif',
    '.scr',
    '.vbs',
    '.js',
    '.jar',
    '.app',
    '.deb',
    '.rpm',
  ];

  static async validateFile(
    file: File,
    category: 'image' | 'video' | 'encrypted'
  ): Promise<FileValidationResult> {
    const warnings: string[] = [];

    const extensionCheck = this.validateExtension(file.name, category);
    if (!extensionCheck.isValid) {
      return extensionCheck;
    }

    const sizeCheck = this.validateSize(file.size, category);
    if (!sizeCheck.isValid) {
      return sizeCheck;
    }

    const mimeCheck = this.validateMimeType(file.type, category);
    if (!mimeCheck.isValid) {
      warnings.push(mimeCheck.error || 'MIME type mismatch');
    }

    const dangerousCheck = this.checkDangerousFile(file.name);
    if (!dangerousCheck.isValid) {
      return dangerousCheck;
    }

    if (category === 'image' || category === 'video') {
      const contentCheck = await this.validateFileContent(file, category);
      if (!contentCheck.isValid) {
        return contentCheck;
      }
    }

    const nameCheck = this.validateFileName(file.name);
    if (!nameCheck.isValid) {
      return nameCheck;
    }

    return {
      isValid: true,
      warnings: warnings.length > 0 ? warnings : undefined,
    };
  }

  private static validateExtension(
    fileName: string,
    category: 'image' | 'video' | 'encrypted'
  ): FileValidationResult {
    const extension = this.getFileExtension(fileName).toLowerCase();

    if (!extension) {
      return {
        isValid: false,
        error: 'File must have an extension',
      };
    }

    const config = this.FILE_TYPE_CONFIGS[category];
    if (!config.extensions.includes(extension)) {
      return {
        isValid: false,
        error: `Invalid file extension for ${category}. Allowed: ${config.extensions.join(', ')}`,
      };
    }

    return { isValid: true };
  }

  private static validateSize(
    fileSize: number,
    category: 'image' | 'video' | 'encrypted'
  ): FileValidationResult {
    if (fileSize === 0) {
      return {
        isValid: false,
        error: 'File is empty',
      };
    }

    const config = this.FILE_TYPE_CONFIGS[category];
    if (fileSize > config.maxSize) {
      return {
        isValid: false,
        error: `File size exceeds maximum allowed (${this.formatSize(config.maxSize)})`,
      };
    }

    return { isValid: true };
  }

  private static validateMimeType(
    mimeType: string,
    category: 'image' | 'video' | 'encrypted'
  ): FileValidationResult {
    if (!mimeType) {
      return {
        isValid: false,
        error: 'File MIME type is missing',
      };
    }

    const config = this.FILE_TYPE_CONFIGS[category];
    if (!config.mimeTypes.includes(mimeType)) {
      return {
        isValid: false,
        error: `Invalid MIME type for ${category}. Got: ${mimeType}`,
      };
    }

    return { isValid: true };
  }

  private static checkDangerousFile(fileName: string): FileValidationResult {
    const extension = this.getFileExtension(fileName).toLowerCase();

    if (this.DANGEROUS_EXTENSIONS.includes(extension)) {
      return {
        isValid: false,
        error: `File type ${extension} is not allowed for security reasons`,
      };
    }

    const suspiciousPatterns = [
      /\.(exe|bat|cmd|scr|vbs|js|jar)$/i,
      /\.\w+\.(exe|bat|cmd)$/i,
      /[\x00-\x1f]/,
    ];

    for (const pattern of suspiciousPatterns) {
      if (pattern.test(fileName)) {
        return {
          isValid: false,
          error: 'File name contains suspicious patterns',
        };
      }
    }

    return { isValid: true };
  }

  private static async validateFileContent(
    file: File,
    category: 'image' | 'video'
  ): Promise<FileValidationResult> {
    try {
      const headerSize = 12;
      const header = await this.readFileHeader(file, headerSize);

      const config = this.FILE_TYPE_CONFIGS[category];
      if (!config.magicNumbers) {
        return { isValid: true };
      }

      const isValid = config.magicNumbers.some((magic) => {
        if (header.length < magic.offset + magic.bytes.length) {
          return false;
        }

        return magic.bytes.every(
          (byte, index) => header[magic.offset + index] === byte
        );
      });

      if (!isValid) {
        return {
          isValid: false,
          error: `File content does not match expected ${category} format`,
        };
      }

      return { isValid: true };
    } catch (error) {
      return {
        isValid: false,
        error: `Failed to validate file content: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  private static validateFileName(fileName: string): FileValidationResult {
    if (fileName.length > 255) {
      return {
        isValid: false,
        error: 'File name is too long (max 255 characters)',
      };
    }

    const invalidChars = /[<>:"|?*\x00-\x1f]/;
    if (invalidChars.test(fileName)) {
      return {
        isValid: false,
        error: 'File name contains invalid characters',
      };
    }

    if (fileName.startsWith('.') || fileName.endsWith('.')) {
      return {
        isValid: false,
        error: 'File name cannot start or end with a dot',
      };
    }

    return { isValid: true };
  }

  private static async readFileHeader(
    file: File,
    bytes: number
  ): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const blob = file.slice(0, bytes);

      reader.onload = (e) => {
        if (!e.target?.result) {
          reject(new Error('Failed to read file header'));
          return;
        }
        const arrayBuffer = e.target.result as ArrayBuffer;
        resolve(new Uint8Array(arrayBuffer));
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(blob);
    });
  }

  private static getFileExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf('.');
    return lastDot === -1 ? '' : fileName.substring(lastDot);
  }

  private static formatSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${Math.round(size * 100) / 100} ${units[unitIndex]}`;
  }

  static getMaxFileSize(category: 'image' | 'video' | 'encrypted'): number {
    return this.FILE_TYPE_CONFIGS[category].maxSize;
  }

  static getAllowedExtensions(category: 'image' | 'video' | 'encrypted'): string[] {
    return this.FILE_TYPE_CONFIGS[category].extensions;
  }

  static getAllowedMimeTypes(category: 'image' | 'video' | 'encrypted'): string[] {
    return this.FILE_TYPE_CONFIGS[category].mimeTypes;
  }
}
